import axios from 'axios';
import { IChannelImportRepository } from '../../application/ports/IChannelImportRepository';
import { ChannelImportDraftEntity, ChannelImportReport, ChannelImportRow } from '../../domain/entities/ChannelImportDraft';
import { advertizerApiHost } from '../../constants/envs';
import { AuthTokenProvider } from '../auth/AuthTokenProvider';

const BASE_PATH = '/api/admin/channel_imports';

const toReport = (raw: any): ChannelImportReport => ({
  created: raw?.created ?? 0,
  updated: raw?.updated ?? 0,
  failed: raw?.failed ?? 0,
  failures: raw?.failures ?? [],
});

const toEntity = (raw: any): ChannelImportDraftEntity => ChannelImportDraftEntity.create({
  uuid: raw.uuid,
  status: raw.status,
  telegramAccountUuid: raw.telegramAccountUuid,
  totalLinks: raw.totalLinks ?? 0,
  processedLinks: raw.processedLinks ?? 0,
  skippedDuplicates: raw.skippedDuplicates ?? 0,
  rows: raw.rows ?? [],
  failures: raw.failures ?? [],
  errorMessage: raw.errorMessage,
  report: raw.report ? toReport(raw.report) : undefined,
  createdAt: raw.createdAt,
});

export class ChannelImportsAxiosRepository implements IChannelImportRepository {
  constructor(private auth: AuthTokenProvider) {}

  private async authHeaders() {
    const token = await this.auth.getToken();
    if (!token) throw new Error('Unauthenticated');
    return { Authorization: `Bearer ${token}` };
  }

  async start(links: string[], telegramAccountUuid: string): Promise<ChannelImportDraftEntity> {
    const headers = await this.authHeaders();
    const res = await axios.post(advertizerApiHost + BASE_PATH, { links, telegramAccountUuid }, { headers });
    return toEntity(res.data);
  }

  async get(uuid: string): Promise<ChannelImportDraftEntity> {
    const headers = await this.authHeaders();
    const res = await axios.get(`${advertizerApiHost}${BASE_PATH}/${uuid}`, { headers });
    return toEntity(res.data);
  }

  async listRecent(): Promise<ChannelImportDraftEntity[]> {
    const headers = await this.authHeaders();
    const res = await axios.get(advertizerApiHost + BASE_PATH, { headers });
    return (res.data.drafts ?? []).map(toEntity);
  }

  async saveRows(uuid: string, rows: ChannelImportRow[]): Promise<ChannelImportDraftEntity> {
    const headers = await this.authHeaders();
    const res = await axios.put(`${advertizerApiHost}${BASE_PATH}/${uuid}/rows`, { rows }, { headers });
    return toEntity(res.data);
  }

  async apply(uuid: string, dryRun: boolean): Promise<ChannelImportReport> {
    const headers = await this.authHeaders();
    const res = await axios.post(`${advertizerApiHost}${BASE_PATH}/${uuid}/apply`, null, { headers, params: { dryRun } });
    return toReport(res.data);
  }
}
